import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, Play, Clock, Heart, UserMinus, BarChart3, Mail, RefreshCw, Calendar } from 'lucide-react';
import { getProfilePicUrl } from '../../config/imageUrls';

const BuilderContainer = styled.div`
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg) 100%);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 24px;
  margin-top: 20px;
  box-shadow: 0 4px 16px rgba(165, 145, 239, 0.4);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(165, 145, 239, 0.6), transparent);
    border-radius: 12px 12px 0 0;
  }
`;

const BuilderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const BuilderTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ScheduleButton = styled.button`
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 10px 18px;
  color: ${props => props.$color || 'var(--primary)'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$hoverBg || 'rgba(165, 145, 239, 0.15)'};
    border-color: ${props => props.$color || 'var(--primary)'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const BuilderContent = styled.div`
  display: flex;
  gap: 24px;
`;

const ActionPalette = styled.div`
  width: 200px;
  flex-shrink: 0;
`;

const PaletteTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
`;

const PaletteActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PaletteAction = styled.div`
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 12px;
  cursor: grab;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: var(--card-bg);
    border-color: var(--primary);
    transform: translateX(4px);
  }

  &:active {
    cursor: grabbing;
    transform: scale(0.98);
  }
`;

const ActionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${props => props.$color || 'rgba(245, 158, 11, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$iconColor || 'var(--warning)'};
  flex-shrink: 0;
`;

const ScriptTypeBadge = styled.div`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.$color || 'rgba(245, 158, 11, 0.2)'};
  color: ${props => props.$textColor || 'var(--warning)'};
  border: 2px solid ${props => props.$borderColor || 'rgba(245, 158, 11, 0.4)'};
`;

const ActionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Canvas = styled.div`
  flex: 1;
  background: var(--bg);
  border: 2px dashed ${props => props.$isDragOver ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 12px;
  padding: 20px;
  min-height: 500px;
  max-height: 85vh;
  overflow-y: auto;
  transition: all 0.2s ease;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(165, 145, 239, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(165, 145, 239, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(165, 145, 239, 0.5);
  }
`;

const CanvasEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #888;
  text-align: center;
  padding: 40px;
`;

const EmptyIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(165, 145, 239, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
  margin-bottom: 16px;
`;

const BlockList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
  align-content: flex-start;
`;

const Block = styled.div`
  background: ${props => props.$isRestart ? 'rgba(165, 145, 239, 0.1)' : 'var(--card-bg)'};
  border: 1px solid ${props => props.$isRestart ? 'rgba(165, 145, 239, 0.4)' : 'var(--card-border)'};
  border-radius: 10px;
  padding: ${props => props.$isRestart ? '10px 6px' : '12px'};
  position: relative;
  transition: all 0.2s ease;
  width: ${props => props.$isRestart ? '60px' : '180px'};
  flex-shrink: 0;
  ${props => props.$isRestart && `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50px;
  `}

  &:hover {
    border-color: var(--primary);
    box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);
    transform: translateY(-2px);
  }
`;

const DeviceBadge = styled.div`
  background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
  border: 1px solid #718096;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #e2e8f0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 80px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.1), 0 1px 2px rgba(165, 145, 239, 0.2);
`;

const ProfilePic = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(245, 158, 11, 0.5);
`;

const ProfilePicPlaceholder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(245, 158, 11, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--warning);
  font-weight: 700;
  border: 2px solid rgba(245, 158, 11, 0.5);
`;

const UsernameLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--warning);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeviceSelect = styled.select`
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 12px 14px;
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(165, 145, 239, 0.1);
  }

  &:hover {
    border-color: var(--primary);
  }

  option {
    background: var(--bg);
    font-size: 15px;
    font-weight: 600;
    padding: 12px;
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  border: 1px solid rgba(242, 63, 67, 0.3);
  border-radius: 6px;
  padding: 6px;
  color: var(--error);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(242, 63, 67, 0.15);
    border-color: var(--error);
    transform: scale(1.1);
  }
`;

const Connector = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary);
  font-size: 18px;
  padding: 0 4px;
  flex-shrink: 0;
  font-weight: 700;
`;

const RestartBadge = styled.div`
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: var(--text-primary);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 6px rgba(165, 145, 239, 0.3);
`;

const PatternSection = styled.div`
  margin-top: 20px;
`;

const PatternHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const PatternLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PatternGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PatternCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: var(--surface);
    border-color: var(--primary);
    transform: translateX(2px);
  }
`;

const PatternCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const PatternCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PatternEmoji = styled.span`
  font-size: 16px;
`;

const PatternName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
`;

const PatternActions = styled.div`
  display: flex;
  gap: 4px;
`;

const PatternActionButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.$danger ? 'rgba(242, 63, 67, 0.3)' : 'var(--card-border)'};
  border-radius: 6px;
  padding: 4px 6px;
  color: ${props => props.$danger ? 'var(--error)' : 'var(--primary)'};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: ${props => props.$danger ? 'rgba(242, 63, 67, 0.15)' : 'rgba(165, 145, 239, 0.15)'};
    border-color: ${props => props.$danger ? 'var(--error)' : 'var(--primary)'};
  }
`;

const PatternSequence = styled.div`
  font-size: 11px;
  color: #888;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const PatternStep = styled.span`
  background: var(--bg);
  padding: 2px 6px;
  border-radius: 4px;
  color: var(--text-secondary);
`;

const CreatePatternButton = styled.button`
  width: 100%;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 10px;
  color: var(--primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;

  &:hover {
    background: rgba(165, 145, 239, 0.15);
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ScheduleModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ScheduleContent = styled.div`
  background: var(--card-bg);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(165, 145, 239, 0.6);
  border: 1px solid var(--card-border);
  animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ScheduleHeader = styled.div`
  padding: 20px 24px;
  background: var(--bg);
  border-bottom: 1px solid var(--card-border);
`;

const ScheduleTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ScheduleBody = styled.div`
  padding: 24px;
`;

const ScheduleForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #ccc;
`;

const Input = styled.input`
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(165, 145, 239, 0.1);
  }

  &:hover {
    border-color: var(--primary);
  }
`;

const ScheduleFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 24px;
  background: var(--bg);
  border-top: 1px solid var(--card-border);
`;

const Button = styled.button`
  background: ${props => props.$primary 
    ? 'linear-gradient(135deg, var(--primary), var(--secondary))' 
    : 'transparent'};
  border: ${props => props.$primary ? 'none' : '1px solid var(--card-border)'};
  border-radius: 8px;
  padding: 12px 24px;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => props.$primary 
      ? 'linear-gradient(135deg, var(--secondary), var(--secondary))' 
      : 'rgba(255, 255, 255, 0.05)'};
    transform: translateY(-1px);
  }
`;

const actionTypes = [
  { id: 'interact', label: 'Interaction', iconName: 'Heart', color: 'rgba(59, 130, 246, 0.2)', iconColor: 'var(--info)' },
  { id: 'unfollow', label: 'Unfollow', iconName: 'UserMinus', color: 'rgba(239, 68, 68, 0.2)', iconColor: 'var(--error)' },
  { id: 'analysis', label: 'Analysis', iconName: 'BarChart3', color: 'rgba(168, 85, 247, 0.2)', iconColor: 'var(--secondary)' },
  { id: 'gmail', label: 'Gmail Check', iconName: 'Mail', color: 'rgba(245, 158, 11, 0.2)', iconColor: 'var(--warning)' }
];

const getIcon = (iconName) => {
  const icons = {
    Heart,
    UserMinus,
    BarChart3,
    Mail,
    RefreshCw
  };
  return icons[iconName];
};

const WorkflowBuilder = ({ workflow, onSave, hasRunningProcesses = false }) => {
  const [blocks, setBlocks] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [selectedPattern, setSelectedPattern] = useState('');
  const [customPatterns, setCustomPatterns] = useState(() => {
    const saved = localStorage.getItem('workflowPatterns');
    return saved ? JSON.parse(saved) : [];
  });
  const [showCreatePatternModal, setShowCreatePatternModal] = useState(false);
  const [newPatternName, setNewPatternName] = useState('');
  const blockIdCounter = React.useRef(0);

  // Patterns de séquences prédéfinis (supprimés)

  const applyPattern = (pattern) => {
    const restarterDeviceId = workflow?.restart_device_id || workflow?.restarter_device_id;
    const newBlocks = [];
    
    // Si le pattern a des blocks sauvegardés (avec devices), les utiliser
    if (pattern.blocks && pattern.blocks.length > 0) {
      pattern.blocks.forEach((savedBlock) => {
        blockIdCounter.current += 1;
        if (savedBlock.isRestart) {
          newBlocks.push({
            ...savedBlock,
            id: `restart_${blockIdCounter.current}`,
            deviceId: restarterDeviceId
          });
        } else {
          newBlocks.push({
            ...savedBlock,
            id: `block_${blockIdCounter.current}`
          });
        }
      });
    } else {
      // Ancien format (juste sequence) - pour compatibilité
      pattern.sequence.forEach((actionId, index) => {
        blockIdCounter.current += 1;
        newBlocks.push({
          id: `restart_${blockIdCounter.current}`,
          type: 'restart',
          label: 'Device Restart',
          iconName: 'RefreshCw',
          isRestart: true,
          deviceId: restarterDeviceId
        });
        
        const actionType = actionTypes.find(a => a.id === actionId);
        if (actionType) {
          blockIdCounter.current += 1;
          newBlocks.push({
            id: `block_${blockIdCounter.current}`,
            type: actionType.id,
            label: actionType.label,
            iconName: actionType.iconName,
            color: actionType.color,
            iconColor: actionType.iconColor,
            deviceId: null,
            accountId: null
          });
        }
      });
    }
    
    setBlocks(newBlocks);
  };

  const handleDragStart = (e, actionType) => {
    e.dataTransfer.setData('actionType', JSON.stringify(actionType));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const actionType = JSON.parse(e.dataTransfer.getData('actionType'));
    
    // Sélectionner un device au hasard
    const availableDevices = workflow.devices.filter(d => d.username);
    const randomDevice = availableDevices.length > 0 
      ? availableDevices[Math.floor(Math.random() * availableDevices.length)]
      : null;
    
    blockIdCounter.current += 1;
    const newBlock = {
      id: `block_${blockIdCounter.current}`,
      type: actionType.id,
      label: actionType.label,
      iconName: actionType.iconName,
      color: actionType.color,
      iconColor: actionType.iconColor,
      deviceId: randomDevice?.id || null,
      accountId: randomDevice?.account_id || null
    };
    
    const newBlocks = [...blocks];
    
    // Récupérer le device ID du restarteur
    const restarterDeviceId = workflow?.restart_device_id || workflow?.restarter_device_id;
    console.log('🔧 Restarter device ID:', restarterDeviceId, 'from workflow:', workflow);
    
    // Ajouter un restart avant le premier bloc
    if (blocks.length === 0) {
      blockIdCounter.current += 1;
      newBlocks.push({
        id: `restart_${blockIdCounter.current}`,
        type: 'restart',
        label: 'Device Restart',
        iconName: 'RefreshCw',
        isRestart: true,
        deviceId: restarterDeviceId
      });
    } else if (blocks.length > 0) {
      // Ajouter un restart avant le nouveau bloc
      blockIdCounter.current += 1;
      newBlocks.push({
        id: `restart_${blockIdCounter.current}`,
        type: 'restart',
        label: 'Device Restart',
        iconName: 'RefreshCw',
        isRestart: true,
        deviceId: restarterDeviceId
      });
    }
    newBlocks.push(newBlock);
    setBlocks(newBlocks);
  };

  const handleDeviceChange = (blockId, deviceId) => {
    const selectedDevice = workflow.devices.find(d => d.id === deviceId);
    console.log(`🔄 Device change for block ${blockId}:`, { deviceId, accountId: selectedDevice?.account_id });
    setBlocks(blocks.map(block => 
      block.id === blockId ? { ...block, deviceId, accountId: selectedDevice?.account_id || null } : block
    ));
  };

  const handleDeleteBlock = (blockId) => {
    const blockIndex = blocks.findIndex(b => b.id === blockId);
    const newBlocks = [...blocks];
    
    // Supprimer le bloc
    newBlocks.splice(blockIndex, 1);
    
    // Si c'était un bloc d'action et qu'il y avait un restart avant, supprimer le restart aussi
    if (blockIndex > 0 && newBlocks[blockIndex - 1]?.isRestart) {
      newBlocks.splice(blockIndex - 1, 1);
    }
    
    setBlocks(newBlocks);
  };

  const handleSave = (mode = 'launch') => {
    // Vérifier que tous les blocs interact ont un device sélectionné
    const blocksWithoutDevice = blocks.filter(b => !b.isRestart && !b.deviceId);
    if (blocksWithoutDevice.length > 0) {
      alert(`⚠️ Veuillez sélectionner un device pour tous les blocs d'action (${blocksWithoutDevice.length} bloc(s) sans device)`);
      return;
    }
    
    const sequence = blocks.map(block => ({
      type: block.type,
      deviceId: block.deviceId,
      accountId: block.accountId,
      isRestart: block.isRestart || false
    }));
    
    console.log('📤 Saving workflow sequence:', { mode, sequence });
    console.log('📋 Blocks data:', blocks);
    
    onSave({
      sequence,
      mode, // 'append', 'launch', 'schedule'
      scheduledDate: scheduledDate || null,
      scheduledTime: scheduledTime || null
    });
  };

  const handleSchedule = () => {
    setShowScheduleModal(true);
  };

  const handleScheduleSave = () => {
    if (!scheduledDate || !scheduledTime) {
      alert('⚠️ Veuillez sélectionner une date et une heure');
      return;
    }
    setShowScheduleModal(false);
    handleSave('schedule');
  };

  const handleSaveAsPattern = () => {
    if (blocks.length === 0) {
      alert('⚠️ Ajoutez des actions avant de créer un pattern');
      return;
    }
    setShowCreatePatternModal(true);
  };

  const handleCreatePattern = () => {
    if (!newPatternName.trim()) {
      alert('⚠️ Veuillez entrer un nom pour le pattern');
      return;
    }

    // Sauvegarder TOUS les blocks (avec devices et accounts)
    const blocksToSave = blocks.map(block => ({
      type: block.type,
      label: block.label,
      iconName: block.iconName,
      color: block.color,
      iconColor: block.iconColor,
      deviceId: block.deviceId,
      accountId: block.accountId,
      isRestart: block.isRestart
    }));

    const actionBlocks = blocks.filter(b => !b.isRestart);
    const sequence = actionBlocks.map(b => b.type);

    const newPattern = {
      id: `custom_${Date.now()}`,
      emoji: '⭐',
      name: newPatternName,
      description: 'Pattern personnalisé',
      sequence,
      blocks: blocksToSave,
      isCustom: true
    };

    const updatedPatterns = [...customPatterns, newPattern];
    setCustomPatterns(updatedPatterns);
    localStorage.setItem('workflowPatterns', JSON.stringify(updatedPatterns));
    
    setShowCreatePatternModal(false);
    setNewPatternName('');
    alert(`✅ Pattern "${newPatternName}" créé avec succès !`);
  };

  const handleDeletePattern = (patternId) => {
    if (!window.confirm('Supprimer ce pattern personnalisé ?')) return;
    
    const updatedPatterns = customPatterns.filter(p => p.id !== patternId);
    setCustomPatterns(updatedPatterns);
    localStorage.setItem('workflowPatterns', JSON.stringify(updatedPatterns));
    
    if (selectedPattern === patternId) {
      setSelectedPattern('');
    }
  };

  return (
    <BuilderContainer>
      <BuilderHeader>
        <BuilderTitle>
          <Play size={20} />
          Créer une séquence d'actions
        </BuilderTitle>
        <div style={{ display: 'flex', gap: '12px' }}>
          <ScheduleButton onClick={handleSchedule}>
            <Calendar size={16} />
            Programmer
          </ScheduleButton>
          <ScheduleButton onClick={() => handleSave('append')} style={{ background: 'rgba(59, 130, 246, 0.2)', color: 'var(--info)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
            <Plus size={16} />
            Ajouter à la séquence
          </ScheduleButton>
          <ScheduleButton 
            onClick={() => {
              if (hasRunningProcesses) {
                alert('⚠️ Un workflow est déjà en cours d\'exécution.\nVeuillez attendre qu\'il se termine ou utilisez "Ajouter à la séquence".');
                return;
              }
              handleSave('launch');
            }} 
            style={{ 
              background: hasRunningProcesses ? 'rgba(100, 100, 100, 0.3)' : 'linear-gradient(135deg, var(--warning), var(--warning))', 
              color: hasRunningProcesses ? '#888' : '#1a1d29', 
              border: 'none',
              cursor: hasRunningProcesses ? 'not-allowed' : 'pointer',
              opacity: hasRunningProcesses ? 0.5 : 1
            }}
            disabled={hasRunningProcesses}
          >
            <Play size={16} />
            {hasRunningProcesses ? 'Workflow en cours...' : 'Lancer maintenant'}
          </ScheduleButton>
        </div>
      </BuilderHeader>

      <BuilderContent>
        <ActionPalette>
          <PaletteTitle>Actions disponibles</PaletteTitle>
          <PaletteActions>
            {actionTypes.map(action => {
              const IconComponent = getIcon(action.iconName);
              return (
                <PaletteAction
                  key={action.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, action)}
                >
                  <ActionIcon $color={action.color} $iconColor={action.iconColor}>
                    <IconComponent size={18} />
                  </ActionIcon>
                  <ActionLabel>{action.label}</ActionLabel>
                </PaletteAction>
              );
            })}
          </PaletteActions>

          {/* Section Patterns */}
          {(customPatterns.length > 0 || blocks.length > 0) && (
            <PatternSection>
              <PatternHeader>
                <PatternLabel>Mes patterns</PatternLabel>
              </PatternHeader>
              {customPatterns.length > 0 && (
                <PatternGrid>
                  {customPatterns.map(pattern => (
                    <PatternCard key={pattern.id}>
                      <PatternCardHeader>
                        <PatternCardTitle onClick={() => applyPattern(pattern)}>
                          <PatternEmoji>{pattern.emoji}</PatternEmoji>
                          <PatternName>{pattern.name}</PatternName>
                        </PatternCardTitle>
                        <PatternActions onClick={(e) => e.stopPropagation()}>
                          <PatternActionButton onClick={() => handleDeletePattern(pattern.id)} $danger>
                            <Trash2 size={12} />
                          </PatternActionButton>
                        </PatternActions>
                      </PatternCardHeader>
                      <PatternSequence>
                        {pattern.blocks ? (
                          pattern.blocks.filter(b => !b.isRestart).map((block, idx) => (
                            <PatternStep key={idx}>
                              {block.label}
                            </PatternStep>
                          ))
                        ) : (
                          pattern.sequence.map((action, idx) => (
                            <PatternStep key={idx}>
                              {actionTypes.find(a => a.id === action)?.label || action}
                            </PatternStep>
                          ))
                        )}
                      </PatternSequence>
                    </PatternCard>
                  ))}
                </PatternGrid>
              )}
              <CreatePatternButton onClick={handleSaveAsPattern} disabled={blocks.length === 0}>
                <Plus size={14} />
                Sauvegarder comme pattern
              </CreatePatternButton>
            </PatternSection>
          )}
        </ActionPalette>

        <Canvas
          $isDragOver={isDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {blocks.length === 0 ? (
            <CanvasEmpty>
              <EmptyIcon>
                <Plus size={32} />
              </EmptyIcon>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                Glissez des actions ici
              </div>
              <div style={{ fontSize: '12px' }}>
                Créez votre séquence en glissant-déposant les actions depuis la palette
              </div>
            </CanvasEmpty>
          ) : (
            <BlockList>
              {blocks.map((block, index) => {
                const BlockIcon = getIcon(block.iconName);
                const selectedDevice = workflow.devices.find(d => d.id === block.deviceId);
                const username = selectedDevice?.username || '';
                const deviceName = selectedDevice?.name || '';
                const profilePicPath = getProfilePicUrl(username);
                
                return (
                  <React.Fragment key={block.id}>
                    <Block $isRestart={block.isRestart}>
                      {block.isRestart ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <BlockIcon size={16} style={{ color: 'var(--warning)' }} />
                          <span style={{ fontSize: '9px', fontWeight: '700', color: 'var(--warning)', textTransform: 'uppercase' }}>Restart</span>
                          <RestartBadge>Auto</RestartBadge>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                            <ScriptTypeBadge 
                              $color={block.color} 
                              $textColor={block.iconColor}
                              $borderColor={block.iconColor}
                              style={{ fontSize: '10px', padding: '3px 8px' }}
                            >
                              <BlockIcon size={12} style={{ marginRight: '4px' }} />
                              {block.label}
                            </ScriptTypeBadge>
                            <div style={{ flex: 1 }} />
                            {deviceName && (
                              <DeviceBadge title={deviceName}>
                                {deviceName}
                              </DeviceBadge>
                            )}
                            <DeleteButton onClick={() => handleDeleteBlock(block.id)} style={{ padding: '4px' }}>
                              <Trash2 size={12} />
                            </DeleteButton>
                          </div>
                          {username ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <ProfilePic 
                                src={profilePicPath}
                                alt={username}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <ProfilePicPlaceholder style={{ display: 'none' }}>
                                {username.charAt(0).toUpperCase()}
                              </ProfilePicPlaceholder>
                              <UsernameLabel>@{username}</UsernameLabel>
                            </div>
                          ) : null}
                          <DeviceSelect
                            value={block.deviceId || ''}
                            onChange={(e) => handleDeviceChange(block.id, e.target.value)}
                          >
                            <option value="">-- Sélectionner un compte --</option>
                            {workflow.devices.map(device => (
                              <option key={device.id} value={device.id}>
                                👤 @{device.username}
                              </option>
                            ))}
                          </DeviceSelect>
                        </>
                      )}
                    </Block>
                    {index < blocks.length - 1 && (
                      <Connector>→</Connector>
                    )}
                  </React.Fragment>
                );
              })}
            </BlockList>
          )}
        </Canvas>
      </BuilderContent>

      {showScheduleModal && (
        <ScheduleModal onClick={() => setShowScheduleModal(false)}>
          <ScheduleContent onClick={(e) => e.stopPropagation()}>
            <ScheduleHeader>
              <ScheduleTitle>
                <Calendar size={24} />
                Programmer l'exécution
              </ScheduleTitle>
            </ScheduleHeader>
            <ScheduleBody>
              <ScheduleForm>
                <FormGroup>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Heure</Label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  />
                </FormGroup>
              </ScheduleForm>
            </ScheduleBody>
            <ScheduleFooter>
              <Button onClick={() => setShowScheduleModal(false)}>Annuler</Button>
              <Button $primary onClick={handleScheduleSave}>
                <Clock size={16} />
                Programmer
              </Button>
            </ScheduleFooter>
          </ScheduleContent>
        </ScheduleModal>
      )}

      {showCreatePatternModal && (
        <ScheduleModal onClick={() => setShowCreatePatternModal(false)}>
          <ScheduleContent onClick={(e) => e.stopPropagation()}>
            <ScheduleHeader>
              <ScheduleTitle>
                <Plus size={24} />
                Créer un pattern personnalisé
              </ScheduleTitle>
            </ScheduleHeader>
            <ScheduleBody>
              <ScheduleForm>
                <FormGroup>
                  <Label>Nom du pattern</Label>
                  <Input
                    type="text"
                    placeholder="Ex: Ma routine quotidienne"
                    value={newPatternName}
                    onChange={(e) => setNewPatternName(e.target.value)}
                    autoFocus
                  />
                </FormGroup>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '12px' }}>
                  <strong>Séquence actuelle :</strong>
                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {blocks.filter(b => !b.isRestart).map((block, idx) => (
                      <div key={block.id} style={{
                        background: 'var(--bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        fontSize: '12px',
                        color: 'var(--btn-text)'
                      }}>
                        {idx + 1}. {block.label}
                      </div>
                    ))}
                  </div>
                </div>
              </ScheduleForm>
            </ScheduleBody>
            <ScheduleFooter>
              <Button onClick={() => setShowCreatePatternModal(false)}>Annuler</Button>
              <Button $primary onClick={handleCreatePattern}>
                <Plus size={16} />
                Créer le pattern
              </Button>
            </ScheduleFooter>
          </ScheduleContent>
        </ScheduleModal>
      )}
    </BuilderContainer>
  );
};

export default WorkflowBuilder;
